import { useMemo, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import * as Icons from "../../../assets/icons/vander";
import SimpleBar from "simplebar-react";
import logoDark from "../../../assets/images/logo-dark.png";
import logoLight from "../../../assets/images/logo-light.png";
import "simplebar-react/dist/simplebar.min.css";
import "../../../assets/css/sidebar.css";
import { useProfiles } from "@modules/profiles/hooks/use-profiles.hook";
import { UserRole } from "@modules/shared/enums/user.enum";

// --- TYPE DEFINITIONS ---

interface SidebarChildItem {
  label: string;
  path: string;
  key: string;
}

interface SidebarItemBase {
  label: string;
  icon: string;
}

interface SidebarItemSingle extends SidebarItemBase {
  path: string;
  keys: string[];
}

interface SidebarItemDropdown extends SidebarItemBase {
  menuKey: string;
  children: SidebarChildItem[];
  path?: string;
}

type SidebarItem = SidebarItemSingle | SidebarItemDropdown;

interface SidebarGroup {
  group: string;
  items: SidebarItem[];
}

type SidebarProps = {
  menuClass?: string;
};

// --- DATA ---

const sidebarData: SidebarGroup[] = [
  {
    group: "Bảng Điều Khiển",
    items: [
      {
        label: "Trang Chủ",
        icon: "GrDashboard",
        path: "/dashboard",
        keys: ["dashboard", ""],
      },
    ],
  },
  {
    group: "Management",
    items: [
      {
        label: "Trainers",
        icon: "GiMuscleUp",
        menuKey: "trainers",
        children: [
          {
            label: "Trainers",
            path: "/trainers",
            key: "trainers",
          },
          {
            label: "Top Trainers",
            path: "/top-trainers",
            key: "top-trainers",
          },
        ],
      },
      {
        label: "Clients",
        icon: "LuUser2",
        menuKey: "client",
        children: [
          {
            label: "Client List",
            path: "/clients",
            key: "clients",
          },
        ],
      },
      {
        label: "Verification",
        icon: "LuUser2",
        menuKey: "verification",
        children: [
          {
            label: "Trainer",
            path: "/trainer-verification",
            key: "trainer-verification",
          },
          {
            label: "Trainer Package",
            path: "/trainer-package-verification",
            key: "trainer-package-verification",
          },
        ],
      },
    ],
  },
  {
    group: "Social",
    items: [
      {
        label: "Engagements",
        icon: "MdLocalActivity",
        menuKey: "engagements-item",
        children: [
          {
            label: "Campaigns",
            path: "/engagements/campaigns",
            key: "campaigns",
          },
          {
            label: "Rewards",
            path: "/engagements/rewards",
            key: "rewards",
          },
        ],
      },
      {
        label: "Posts",
        icon: "FaRegNewspaper",
        menuKey: "admin-post",
        children: [
          {
            label: "Admin Posts",
            path: "/admin-post",
            key: "admin-post",
          },
        ],
      },
      {
        label: "Media",
        icon: "MdPermMedia",
        menuKey: "media-item",
        children: [
          {
            label: "Onboarding",
            path: "/onboarding-media",
            key: "onboarding-media",
          },
          {
            label: "System",
            path: "/system-media",
            key: "system-media",
          },
        ],
      },
    ],
  },
  {
    group: "Đối Tác B2B",
    items: [
      {
        label: "Thương Hiệu",
        icon: "MdOutlineBrandingWatermark",
        keys: ["brands"],
        path: "/b2b/brands",
      },
      {
        label: "Nhãn Hàng",
        icon: "MdOutlineStorefront",
        path: "/b2b/stores",
        keys: ["stores"],
      },
      {
        label: "Phòng Gym",
        icon: "MdOutlineFitnessCenter",
        path: "/b2b/gyms",
        keys: ["gyms"],
      },
    ],
  },
  {
    group: "Báo cáo",
    items: [
      {
        label: "Báo cáo",
        icon: "MdReport",
        path: "/report",
        keys: ["report"],
      },
    ],
  },
  {
    group: "Policy",
    items: [
      {
        label: "Pages",
        icon: "MdPolicy",
        menuKey: "pages",
        children: [
          {
            label: "Term Policy",
            path: "/term-policy",
            key: "term-policy",
          },
          {
            label: "Privacy Policy",
            path: "/privacy-policy",
            key: "privacy-policy",
          },
        ],
      },
    ],
  },
  {
    group: "Setting",
    items: [
      {
        label: "System Setting",
        icon: "MdDisplaySettings",
        menuKey: "systemSetting",
        children: [
          {
            label: "Open AI Assistant",
            path: "/system-setting/openAI-assistant",
            key: "openAI-assistant",
          },
          {
            label: "MongoDB",
            path: "/system-setting/mongodb",
            key: "mongodb",
          },
          {
            label: "VietGuys ZNS",
            path: "/system-setting/vietguys",
            key: "vietguys",
          },
        ],
      },
    ],
  },
  {
    group: "Feature Management",
    items: [
      {
        label: "Manage Feature",
        icon: "MdFeaturedPlayList",
        path: "/feature-flag",
        keys: ["feature-flag"],
      },
      {
        label: "App Version — Force Update",
        icon: "MdSystemUpdateAlt",
        path: "/app-version",
        keys: ["app-version"],
      },
    ],
  },
];

const RESTRICTED_GROUPS = new Set(["Setting", "Feature Management"]);

export default function Sidebar({
  menuClass = "sidebar-wrapper sidebar-dark",
}: Readonly<SidebarProps>) {
  const { loadProfile } = useProfiles();
  const location = useLocation();
  const currentPath = location.pathname;
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [adminLevel, setAdminLevel] = useState<UserRole | null>(null);

  useEffect(() => {
    const fetchAdminLevel = async () => {
      try {
        const data = await loadProfile("me");
        if (data?.role) {
          setAdminLevel(data.role);
        }
      } catch (err) {
        console.error("Failed to load profile for sidebar permissions", err);
      }
    };

    fetchAdminLevel();
  }, [loadProfile]);

  const isSuperAdmin = adminLevel === UserRole.SUPER_ADMIN;
  const isVerifiedPartner = adminLevel === UserRole.VERIFIED_PARTNER;

  const visibleSidebarData = useMemo(() => {
    if (isVerifiedPartner) {
      return [
        {
          group: "Management",
          items: [
            {
              label: "Trainers",
              icon: "GiMuscleUp",
              path: "/trainers",
              keys: ["trainers"],
            },
            {
              label: "Trainer Verification",
              icon: "LuUser2",
              path: "/trainer-verification",
              keys: ["trainer-verification"],
            },
          ],
        },
      ];
    }

    return sidebarData.filter((group) => {
      if (RESTRICTED_GROUPS.has(group.group)) {
        return isSuperAdmin;
      }
      return true;
    });
  }, [isVerifiedPartner, isSuperAdmin]);

  // ===== Hàm kiểm tra active dựa trên path =====
  const isActivePath = (path: string): boolean => {
    if (path === "/dashboard") {
      return currentPath === "/dashboard" || currentPath === "/";
    }
    return currentPath === path || currentPath.startsWith(path + "/");
  };

  const isActiveChild = (child: SidebarChildItem): boolean => {
    return isActivePath(child.path);
  };

  // Tự động mở submenu nếu có child active
  useEffect(() => {
    let foundMenuKey: string | null = null;
    for (const group of visibleSidebarData) {
      for (const item of group.items) {
        if ("children" in item && item.children) {
          const hasActiveChild = item.children.some((child) =>
            isActiveChild(child)
          );
          if (hasActiveChild) {
            foundMenuKey = item.menuKey;
            break;
          }
        }
      }
      if (foundMenuKey) break;
    }
    if (foundMenuKey && openSubmenu !== foundMenuKey) {
      setOpenSubmenu(foundMenuKey);
    }
  }, [currentPath, visibleSidebarData]);

  const renderIcon = (iconName: string) => {
    const IconComponent = (Icons as any)[iconName];
    return IconComponent ? (
      <IconComponent className="me-2 d-inline-block mb-0 icon" />
    ) : null;
  };

  const renderMenu = (item: SidebarItem) => {
    // Dropdown item
    if ("children" in item && item.children) {
      const hasActiveChild = item.children.some((child) =>
        isActiveChild(child)
      );
      const isOpen = openSubmenu === item.menuKey || hasActiveChild;

      return (
        <li className="sidebar-dropdown ms-0" key={item.label}>
          <Link
            to={item.path || "#"}
            onClick={() =>
              setOpenSubmenu((prev) =>
                prev === item.menuKey ? null : item.menuKey
              )
            }
          >
            {renderIcon(item.icon)}
            {item.label}
          </Link>

          <div className={`sidebar-submenu ${isOpen ? "d-block" : ""}`}>
            <ul>
              {item.children.map((child) => {
                const active = isActiveChild(child);
                return (
                  <li key={child.key} className={active ? "active" : ""}>
                    <Link to={child.path}>{child.label}</Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </li>
      );
    }

    // Single item – ép kiểu an toàn vì chỉ có thể là SidebarItemSingle
    const singleItem = item as SidebarItemSingle;
    const active = isActivePath(singleItem.path);
    return (
      <li key={singleItem.label} className={active ? "active" : ""}>
        <Link to={singleItem.path}>
          {renderIcon(singleItem.icon)}
          {singleItem.label}
        </Link>
      </li>
    );
  };

  return (
    <nav id="sidebar" className={`sidebar-wrapper sidebar-dark ${menuClass}`}>
      <SimpleBar
        className="sidebar-content"
        data-simplebar
        style={{ height: "calc(100% - 60px)" }}
      >
        <div className="sidebar-brand">
          <Link to="/dashboard">
            <img
              src={logoDark}
              height={22}
              className="logo-light-mode"
              alt=""
            />
            <img
              src={logoLight}
              height={22}
              className="logo-dark-mode"
              alt=""
            />
            <span className="sidebar-colored">
              <img src={logoLight} height={22} alt="" />
            </span>
          </Link>
        </div>

        <ul className="sidebar-menu">
          {visibleSidebarData.map((group) => (
            <li key={group.group} className="sidebar-group">
              <div className="sidebar-group-title">{group.group}</div>
              <ul className="sidebar-group-items">
                {group.items.map((item) => renderMenu(item))}
              </ul>
              <hr className="sidebar-divider" />
            </li>
          ))}
        </ul>
      </SimpleBar>

      <ul className="sidebar-footer list-unstyled mb-0">
        <li className="list-inline-item mb-0 ms-1">
          <Link to="#" className="btn btn-icon btn-pills btn-soft-primary">
            <Icons.FaRegComment />
          </Link>
        </li>
      </ul>
    </nav>
  );
}